import { useState } from "react";
import { Check, Loader2, Mail, MapPin, Phone, Video, Users, PhoneCall } from "lucide-react";
import { toast } from "sonner";
import { motion } from "framer-motion";
import Seo from "@/components/site/Seo";
import PageHero from "@/components/site/PageHero";
import { Reveal } from "@/components/site/motion";
import { Calendar } from "@/components/ui/calendar";
import { destinations } from "@/data/destinations";
import { services } from "@/data/services";
import { site, whatsappLink, countryCodes, telHref } from "@/data/content";
import OfficeMaps from "@/components/site/OfficeMaps";
import OfficeHours from "@/components/site/OfficeHours";
import { http, errMsg } from "@/lib/api";

const MODES = [["in-person", "In person", Users], ["video", "Video", Video], ["phone", "Phone", PhoneCall]];
const today = new Date(new Date().setHours(0, 0, 0, 0));
const hhmm = (m) => `${String(Math.floor(m / 60)).padStart(2, "0")}:${String(m % 60).padStart(2, "0")}`;
const slotsFor = (o) => {
  const out = [];
  for (let m = o.open; m <= o.close - 60; m += 90) out.push(hhmm(m));
  return out;
};

const Field = ({ label, children, className = "" }) => (
  <label className={`block ${className}`}><span className="label-lux">{label}</span>{children}</label>
);

function BookingForm({ onDone }) {
  const [f, setF] = useState({ country_code: "+91", mode: "video", destination: "", service: "", office: site.offices[0].city });
  const [date, setDate] = useState();
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setF((p) => ({ ...p, [k]: e.target.value }));
  const inPerson = f.mode === "in-person";
  const office = inPerson ? site.offices.find((o) => o.city === f.office) : site.offices[0];
  const slots = slotsFor(office);
  const dayOff = (d) => d < today || !office.days.includes(d.getDay());
  const pick = (patch) => {
    setF((p) => ({ ...p, ...patch, time: undefined }));
    setDate(undefined);
  };

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const d = date ? `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}` : null;
      await http.post("/leads", { type: "contact", ...f, office: inPerson ? f.office : null, preferred_date: d, preferred_time: f.time ? `${f.time} ${office.tzLabel}` : null });
      onDone(f.name);
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <form onSubmit={submit} className="grid gap-x-12 gap-y-12 md:grid-cols-2" data-testid="contact-form">
      <Field label="Full name"><input required minLength={2} className="input-lux" value={f.name || ""} onChange={set("name")} data-testid="contact-name-input" /></Field>
      <Field label="Email"><input required type="email" className="input-lux" value={f.email || ""} onChange={set("email")} data-testid="contact-email-input" /></Field>
      <Field label="Phone">
        <div className="flex gap-3">
          <select aria-label="Country code" className="input-lux !w-24" value={f.country_code} onChange={set("country_code")} data-testid="contact-country-code-select">{countryCodes.map((c) => <option key={c}>{c}</option>)}</select>
          <input required minLength={5} type="tel" className="input-lux" value={f.phone || ""} onChange={set("phone")} data-testid="contact-phone-input" />
        </div>
      </Field>
      <Field label="Country of interest">
        <select required className="input-lux" value={f.destination} onChange={set("destination")} data-testid="contact-destination-select">
          <option value="" disabled>Select a destination</option>
          {destinations.map((d) => <option key={d.slug}>{d.name}</option>)}<option>Not yet decided</option>
        </select>
      </Field>
      <Field label="Service of interest" className="md:col-span-2">
        <select required className="input-lux" value={f.service} onChange={set("service")} data-testid="contact-service-select">
          <option value="" disabled>Select a service</option>
          {services.map((s) => <option key={s.slug}>{s.name}</option>)}
        </select>
      </Field>
      <div className="md:col-span-2">
        <span className="label-lux">Preferred consultation mode</span>
        <div className="mt-3 grid grid-cols-3 gap-3">
          {MODES.map(([v, l, I]) => (
            <button key={v} type="button" onClick={() => pick({ mode: v })} aria-pressed={f.mode === v} data-testid={`contact-mode-${v}`} className={`flex flex-col items-center gap-2 border py-5 text-[12px] font-semibold uppercase tracking-[0.15em] transition-colors duration-500 ${f.mode === v ? "border-navy bg-navy text-ivory" : "border-navy/15 text-navy hover:border-gold"}`}><I className="h-5 w-5" strokeWidth={1.25} />{l}</button>
          ))}
        </div>
      </div>
      {inPerson && (
        <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }} className="md:col-span-2" data-testid="contact-office-picker">
          <span className="label-lux">Office</span>
          <div className="mt-3 grid gap-3 sm:grid-cols-2">
            {site.offices.map((o) => (
              <button key={o.city} type="button" onClick={() => pick({ office: o.city })} aria-pressed={f.office === o.city} data-testid={`contact-office-option-${o.city.toLowerCase()}`} className={`border px-6 py-5 text-left transition-colors duration-500 ${f.office === o.city ? "border-navy bg-navy text-ivory" : "border-navy/15 text-navy hover:border-gold"}`}>
                <span className="block font-serif text-2xl font-light">{o.city}</span>
                <span className={`mt-1 block text-[12px] ${f.office === o.city ? "text-ivory/75" : "text-charcoal-soft"}`}>{o.lines[0]} · {o.hoursText} ({o.tzLabel})</span>
              </button>
            ))}
          </div>
        </motion.div>
      )}
      <div className="md:col-span-2">
        <span className="label-lux">Preferred date & time</span>
        <div className="mt-6 grid gap-10 border-y border-navy/10 py-8 sm:grid-cols-[auto_1fr]">
          <Calendar mode="single" selected={date} onSelect={(d) => { setDate(d); setF((p) => ({ ...p, time: undefined })); }} disabled={dayOff} data-testid="contact-calendar" />
          <div>
            <p className="font-serif text-xl text-navy">{date ? date.toLocaleDateString("en-GB", { weekday: "long", day: "numeric", month: "long" }) : "Select a date"}</p>
            <div className="mt-4 grid grid-cols-3 gap-2">
              {slots.map((s) => <button key={s} type="button" disabled={!date} onClick={() => setF((p) => ({ ...p, time: s }))} data-testid={`contact-slot-${s.replace(":", "")}`} className={`border py-3 text-[13px] transition-colors disabled:opacity-40 ${f.time === s ? "border-gold bg-gold text-navy" : "border-navy/15 text-navy hover:border-gold"}`}>{s}</button>)}
            </div>
            <p className="mt-4 text-[12px] text-charcoal-soft" data-testid="contact-slot-note">{inPerson ? `${office.city} office · ${office.hoursText}. Times shown in ${office.tzLabel}.` : "Times shown in IST."} Your adviser will confirm the appointment.</p>
          </div>
        </div>
      </div>
      <Field label="Message" className="md:col-span-2"><textarea rows={4} className="input-lux resize-none" value={f.message || ""} onChange={set("message")} placeholder="Tell us briefly about your plans" data-testid="contact-message-input" /></Field>
      <button type="submit" disabled={busy} className="btn-navy md:col-span-2" data-testid="contact-submit-btn">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Request consultation"}</button>
    </form>
  );
}

export default function Contact() {
  const [done, setDone] = useState(null);
  return (
    <>
      <Seo title="Book a Private Consultation" description="Arrange a confidential consultation with Immistop in Ahmedabad, by video or by phone. Speak with a senior adviser about your global plans." />
      <PageHero compact eyebrow="Contact" lines={["Book a private", <em key="e" className="italic text-gold">consultation.</em>]} image="/img/jet-3.jpg" testid="contact-hero" />
      <section className="section-lux bg-ivory">
        <div className="container-lux grid gap-24 lg:grid-cols-[1.4fr_0.8fr] lg:gap-28">
          <Reveal>
            {done ? (
              <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="py-16 text-center" data-testid="contact-success">
                <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full border border-gold-deep text-gold-deep"><Check className="h-7 w-7" /></div>
                <h2 className="mt-8 font-serif text-4xl text-navy">Thank you, {done.split(" ")[0]}.</h2>
                <p className="mx-auto mt-4 max-w-md text-charcoal-soft">Your request has been received in confidence. An adviser will be in touch shortly to confirm your consultation.</p>
              </motion.div>
            ) : <BookingForm onDone={setDone} />}
          </Reveal>
          <aside className="space-y-16" data-testid="contact-office-details">
            <div>
              <p className="eyebrow-dark">Our Offices</p>
              <div className="mt-8 space-y-10">
                {site.offices.map((o) => (
                  <div key={o.label} className="border-t border-navy/10 pt-8" data-testid={`contact-office-${o.city.toLowerCase()}`}>
                    <p className="font-serif text-3xl font-light text-navy">{o.label}</p>
                    <ul className="mt-5 space-y-4 text-[15px] text-charcoal">
                      <li className="flex gap-4"><MapPin className="mt-0.5 h-5 w-5 shrink-0 text-gold-deep" /><span>{o.lines.map((l) => <span key={l} className="block">{l}</span>)}</span></li>
                      <li className="flex gap-4"><Phone className="h-5 w-5 shrink-0 text-gold-deep" /><a href={telHref(o.phone)} className="link-lux" data-testid={`contact-phone-${o.city.toLowerCase()}`}>{o.phone}</a></li>
                      <li><OfficeHours office={o} /></li>
                    </ul>
                  </div>
                ))}
                <p className="flex gap-4 border-t border-navy/10 pt-8 text-[15px] text-charcoal"><Mail className="h-5 w-5 shrink-0 text-gold-deep" /><a href={`mailto:${site.email}`} className="link-lux">{site.email}</a></p>
              </div>
              <a href={whatsappLink()} target="_blank" rel="noopener noreferrer" className="btn-gold mt-10 w-full" data-testid="contact-whatsapp-btn"><span>WhatsApp {site.whatsappDisplay}</span></a>
            </div>
            <OfficeMaps />
          </aside>
        </div>
      </section>
    </>
  );
}
